/**
 * ResponsiveUtils — Helpers pour layouts adaptatifs
 *
 * Objectif : garantir que tous les écrans s'adaptent proprement aux
 * appareils iOS + Android, du plus petit (iPhone SE 4.7") à la tablette
 * (iPad Pro 12.9"). Évite les débordements de texte, les boutons coupés,
 * les images qui cassent la mise en page.
 *
 * Base de design : iPhone 13/14 (390 × 844)
 *
 * Usage :
 *   import { scale, fontScale, isSmallPhone, isTablet, screenPadding } from '../utils/ResponsiveUtils';
 *
 *   // Dimensions proportionnelles
 *   <View style={{ width: scale(100), height: scale(100) }} />
 *
 *   // Police avec clamp plus serré pour garder la lisibilité
 *   <Text style={{ fontSize: fontScale(16) }}>...</Text>
 *
 *   // Layout conditionnel
 *   {isSmallPhone && <PetiteCarte />}
 *   {isTablet    && <GrilleTablette />}
 */

import { Dimensions, PixelRatio, Platform } from 'react-native';

/* ══════════════════════════════════════════════════════════════════════
   1. DIMENSIONS DE BASE
   ══════════════════════════════════════════════════════════════════════ */

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Base design = iPhone 13/14 (390 × 844)
const BASE_WIDTH  = 390;
const BASE_HEIGHT = 844;

/* ══════════════════════════════════════════════════════════════════════
   2. BREAKPOINTS
   ══════════════════════════════════════════════════════════════════════ */

/** Téléphones très compacts : iPhone SE 1st gen (320), petit Android (<360) */
export const isSmallPhone   = SCREEN_WIDTH < 360;

/** Téléphones standards : iPhone SE 2/3, iPhone 12/13/14, Pixel 6/7/8, Galaxy S */
export const isStandardPhone = SCREEN_WIDTH >= 360 && SCREEN_WIDTH < 430;

/** Grands téléphones : iPhone Pro Max, Note, Ultra */
export const isLargePhone   = SCREEN_WIDTH >= 430 && SCREEN_WIDTH < 600;

/** Tablettes : iPad, iPad Pro, tablettes Android */
export const isTablet       = SCREEN_WIDTH >= 600;

/** Écrans larges : iPad landscape, grosse tablette */
export const isWideTablet   = SCREEN_WIDTH >= 900;

/** Catégorie unifiée */
export const deviceCategory = isSmallPhone ? 'smallPhone'
  : isStandardPhone ? 'standardPhone'
  : isLargePhone    ? 'largePhone'
  : isWideTablet    ? 'wideTablet'
  : 'tablet';

/* ══════════════════════════════════════════════════════════════════════
   3. SCALE FUNCTIONS
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Scale proportionnel à la largeur de l'écran.
 * Clampé entre 0.85× (petit phone) et 1.4× (tablette) pour éviter
 * des éléments trop petits ou trop grands.
 */
export function scale(size) {
  if (typeof size !== 'number') return size;
  const ratio = Math.min(1.4, Math.max(0.85, SCREEN_WIDTH / BASE_WIDTH));
  return Math.round(size * ratio);
}

/**
 * Scale vertical (basé sur la hauteur).
 * Utile pour des hauteurs fixes (headers, cards).
 */
export function verticalScale(size) {
  if (typeof size !== 'number') return size;
  const ratio = Math.min(1.3, Math.max(0.85, SCREEN_HEIGHT / BASE_HEIGHT));
  return Math.round(size * ratio);
}

/**
 * Scale modéré pour les tailles de police. Clamp plus serré
 * pour garder une bonne lisibilité sur tous les appareils.
 * Ne scale pas pour respecter l'accessibilité (les users qui
 * ont agrandi la police système doivent voir plus grand).
 */
export function fontScale(size) {
  if (typeof size !== 'number') return size;
  // Clamp [0.92, 1.15] : texte reste lisible sans dominer l'écran
  const ratio = Math.min(1.15, Math.max(0.92, SCREEN_WIDTH / BASE_WIDTH));
  const base = Math.round(size * ratio);
  // Sur tablette, augmenter un peu plus pour lisibilité
  if (isTablet) return Math.round(base * 1.08);
  return base;
}

/**
 * Scale "modéré" pour padding/margin. Entre linéaire et fixe
 * pour éviter des espacements énormes sur tablette.
 */
export function moderateScale(size, factor = 0.5) {
  if (typeof size !== 'number') return size;
  return size + (scale(size) - size) * factor;
}

/* ══════════════════════════════════════════════════════════════════════
   4. DIMENSIONS ADAPTATIVES PRÉDÉFINIES
   ══════════════════════════════════════════════════════════════════════ */

/** Padding horizontal des screens */
export const screenPadding = isSmallPhone ? 12
  : isTablet ? 24
  : 16;

/** Gap entre cards d'une grille */
export const gridGap = isSmallPhone ? 8 : isTablet ? 16 : 12;

/** Nombre de colonnes pour grille de produits/articles */
export const productGridCols = isSmallPhone ? 2
  : isStandardPhone ? 2
  : isLargePhone ? 2
  : isWideTablet ? 4
  : 3; // tablet portrait

/** Hauteur standard d'un bouton primaire */
export const buttonHeight = isSmallPhone ? 44 : 48;

/** Hauteur d'un input text */
export const inputHeight = isSmallPhone ? 42 : 46;

/** Hauteur d'une card produit (horizontal scroll) */
export const cardProductHeight = isSmallPhone ? 200 : isTablet ? 260 : 220;

/** Largeur d'une card produit (horizontal scroll) */
export const cardProductWidth = isSmallPhone ? 130 : isTablet ? 170 : 150;

/* ══════════════════════════════════════════════════════════════════════
   5. HELPERS SÉCURITÉ TEXTE
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Props standard pour un Text qui peut déborder.
 * À spread sur tous les <Text> contenant du contenu dynamique
 * (noms de produits, commentaires, emails, etc.).
 *
 * Exemple :
 *   <Text style={s.productName} {...textOverflowProps(2)}>{product.name}</Text>
 */
export function textOverflowProps(numberOfLines = 1) {
  return {
    numberOfLines,
    ellipsizeMode: 'tail',
    // allowFontScaling: true par défaut — respecte l'accessibilité
  };
}

/**
 * Calcule une largeur max pour un Text dans un flex row pour empêcher
 * qu'il pousse les autres éléments.
 */
export function flexShrinkText() {
  return {
    flexShrink: 1,
    flexGrow:   0,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   6. UTILS POUR HEIGHT/WIDTH ABSOLUES
   ══════════════════════════════════════════════════════════════════════ */

export const SCREEN = {
  width:  SCREEN_WIDTH,
  height: SCREEN_HEIGHT,
};

/**
 * Pourcentage de la largeur d'écran (en nombre, pas string).
 * Utile pour les animations ou mesures précises.
 */
export function wp(percentage) {
  return Math.round((percentage / 100) * SCREEN_WIDTH);
}

/**
 * Pourcentage de la hauteur d'écran.
 */
export function hp(percentage) {
  return Math.round((percentage / 100) * SCREEN_HEIGHT);
}

/* ══════════════════════════════════════════════════════════════════════
   7. DEBUG LOG (utile pour vérifier sur quel device on est)
   ══════════════════════════════════════════════════════════════════════ */

export function logDeviceInfo() {
  console.log('[ResponsiveUtils] device info:', {
    width:       SCREEN_WIDTH,
    height:      SCREEN_HEIGHT,
    pixelRatio:  PixelRatio.get(),
    fontScale:   PixelRatio.getFontScale(),
    category:    deviceCategory,
    platform:    Platform.OS,
    version:     Platform.Version,
  });
}

export default {
  scale,
  verticalScale,
  fontScale,
  moderateScale,
  screenPadding,
  gridGap,
  productGridCols,
  buttonHeight,
  inputHeight,
  cardProductHeight,
  cardProductWidth,
  textOverflowProps,
  flexShrinkText,
  SCREEN,
  wp,
  hp,
  isSmallPhone,
  isStandardPhone,
  isLargePhone,
  isTablet,
  isWideTablet,
  deviceCategory,
  logDeviceInfo,
};
